import type { FC } from "react"
import React, { Suspense, useEffect, useState } from "react"
import { Loading } from "@bancacfplus/Loading"
import { Menu } from "../Menu/Menu"
import style from "./PageLayout.module.scss"
import { ErrorBoundary } from "../ErrorBoundary"
import { UserNotAllowed } from "../UserNotAllowed"
import { useTokenData } from "../../hooks/useTokenData"
import { useLocation } from "react-router-dom"

type PageLayoutProps = {
  children: React.ReactNode
}

type PageContainerProps = {
  children: React.ReactNode
}

export const PageContainer: FC<PageContainerProps> = ({ children }) => {
  return (
    <div id="main-content" className={style.pageContent} tabIndex={-1}>
      {children}
    </div>
  )
}

export const PageLayout: FC<PageLayoutProps> = ({ children }) => {
  const { data, error, isLoading } = useTokenData()
  const { pathname } = useLocation()
  const isPartner = data?.partnerRole === "owner" && data.auth0Role.includes("User_PiattaformaMediatori")
  const [pageAnnouncement, setPageAnnouncement] = useState("")

  useEffect(() => {
    let timer: number | undefined

    const raf = window.requestAnimationFrame(() => {
      const pageContent = document.getElementById("main-content")
      if (!pageContent) return

      const heading = pageContent.querySelector<HTMLElement>("h1")
      const pageName = heading?.textContent?.trim()

      if (pageName) {
        setPageAnnouncement("")
        timer = window.setTimeout(() => {
          setPageAnnouncement(`Pagina ${pageName}`)
        }, 20)
      } else {
        setPageAnnouncement("")
      }

      if (heading) {
        const hadTabIndex = heading.hasAttribute("tabindex")
        if (!hadTabIndex) {
          heading.setAttribute("tabindex", "-1")
        }

        heading.focus()

        if (!hadTabIndex) {
          heading.addEventListener(
            "blur",
            () => {
              heading.removeAttribute("tabindex")
            },
            { once: true }
          )
        }
        return
      }

      pageContent.focus()
    })

    return () => {
      window.cancelAnimationFrame(raf)
      if (timer) {
        window.clearTimeout(timer)
      }
    }
  }, [pathname])

  if (isLoading) return <Loading />

  if (error) return <ErrorBoundary error={error} />

  if (data) {
    return (
      <Suspense fallback={<Loading />}>
        <ErrorBoundary>
          {isPartner ? (
            <>
              <div className="u-visually-hidden" aria-live="polite" aria-atomic="true">
                {pageAnnouncement}
              </div>
              <a href="#main-content" className={style.skipLink}>
                Salta al contenuto principale
              </a>
              <Menu />
              <PageContainer>{children}</PageContainer>
            </>
          ) : (
            <>
              <div className="u-visually-hidden" aria-live="polite" aria-atomic="true">
                {pageAnnouncement}
              </div>
              <a href="#main-content" className={style.skipLink}>
                Salta al contenuto principale
              </a>
              <Menu />
              <PageContainer>
                <UserNotAllowed />
              </PageContainer>
            </>
          )}
        </ErrorBoundary>
      </Suspense>
    )
  }

  return <></>
}
